import { Box, Paper, styled, Typography } from "@mui/material";
import {
  RecoveryFlow,
  RecoveryFlowState,
  UpdateRecoveryFlowBody,
} from "@ory/kratos-client";
import { useSetState } from "ahooks";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../api";
import { FullPageLoader } from "../../layout";
import { getOrInitiateRecoveryFlow, submitRecovery } from "./api";
import { RecoveryForm } from "./components";

const FullPageContainer = styled(Box)({
  width: "100vw",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

interface RecoveryPageState {
  data: RecoveryFlow | null;
  loading: boolean;
  error: Error | null;
}

export const RecoveryPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get("flow");
  const returnTo = searchParams.get("return_to");

  const [recoveryFlow, setRecoveryFlow] = useSetState<RecoveryPageState>({
    data: null,
    loading: true,
    error: null,
  });
  const [forceFormRenderKey, setForceFormRender] = useState<number>(0);

  function handleRecoverySuccess(returnTo?: string | null) {
    document.location.href = returnTo ?? "/";
  }

  useLayoutEffect(() => {
    const bgClassName = "green-bg";
    document.body.classList.add(bgClassName);
    return function () {
      document.body.classList.remove(bgClassName);
    };
  });

  async function load(
    flowId: string | null,
    returnTo: string | null
  ): Promise<RecoveryFlow | null> {
    try {
      const recoveryFlow = await getOrInitiateRecoveryFlow(api, {
        ...(flowId && { flowId }),
        ...(returnTo && { returnTo }),
      });
      if (!recoveryFlow) {
        // Already logged in, so a login flow could not be created
        // handleLoginSuccess();
      }
      return recoveryFlow;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  useEffect(() => {
    setRecoveryFlow({
      loading: true,
      error: null,
    });
    load(flowId, returnTo)
      .then(
        (newLoginFlow: RecoveryFlow | null) => {
          setRecoveryFlow({
            data: newLoginFlow,
            error: null,
          });
        },
        (err) => {
          setRecoveryFlow({
            data: null,
            error: err,
          });
        }
      )
      .finally(() => {
        setRecoveryFlow({
          loading: false,
        });
        setForceFormRender((prev) => prev + 1);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId, returnTo]);

  async function attemptRecovery(
    values: UpdateRecoveryFlowBody
  ): Promise<void> {
    if (!recoveryFlow?.data) {
      // Should not happen
      // Avoid TS compilation errors
      return;
    }

    const newRecoveryFlow = await submitRecovery(
      api,
      recoveryFlow.data.id,
      values
    );
    if (newRecoveryFlow) {
      setRecoveryFlow({ data: newRecoveryFlow });
      setForceFormRender((prev) => prev + 1);
      if (newRecoveryFlow.state === RecoveryFlowState.PassedChallenge) {
        handleRecoverySuccess(newRecoveryFlow.return_to);
      }
    } else {
      handleRecoverySuccess(returnTo ?? "/");
    }
  }

  if (!recoveryFlow.data) {
    return <FullPageLoader />;
  }

  return (
    <FullPageContainer sx={{ p: 2 }}>
      <Paper
        sx={{
          padding: 3,
          width: 400,
          marginTop: 3,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <Box sx={{ mb: "24px" }}>
          <Typography variant="h6" textAlign={"center"}>
            {recoveryFlow.data.state === "sent_email"
              ? "Resetovanje lozinke"
              : "Zaboravljena lozinka?"}
          </Typography>
          <Typography
            variant="body1"
            sx={{ marginTop: "8px", textAlign: "center" }}
          >
            {recoveryFlow.data.state === "sent_email"
              ? "Kod za resetovanje lozinke je poslat na tvoj email. Ako ga ne vidiš, proveri spam ili da li si uneo ispravnu adresu."
              : "Bez brige - unesi svoj email, a mi ćemo ti poslati kod za resetovanje lozinke."}
          </Typography>
        </Box>
        <RecoveryForm
          key={forceFormRenderKey}
          flow={recoveryFlow.data}
          onAttemptRecovery={attemptRecovery}
        />
      </Paper>
    </FullPageContainer>
  );
};
